import { useForm } from '@form-ts/react';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useAction } from 'src/hooks/useAction';
import { useParametricSelector } from 'src/hooks/useParametricSelector';
import { BASEDATA_CHANGE } from 'src/modules/basedata/actions/BasedataActions';
import { BASEDATA_FORM_SCHEMA } from 'src/modules/basedata/constants/schema';
import { getBasedataFormData } from 'src/modules/basedata/selectors/getBasedataFormData';
import { BasedataFormData } from 'src/modules/basedata/types/BasedataFormData';
import { BasedataFormView } from 'src/modules/basedata/views/BasedataFormView';
import { Breadcrumb } from 'src/modules/common/components/Breadcrumb';
import { BreadcrumbContainer } from 'src/modules/common/components/BreadcrumbContainer';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { AppLayout } from 'src/modules/common/layouts/AppLayout';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { getHvacFormResolver } from 'src/modules/hvac/selectors/getHvacFormResolver';
import { getAnnualSavingPlaceholderValue } from 'src/modules/project-view/selectors/getAnnualSavingPlaceholderValue';
import { getProjectClosed } from 'src/modules/project-view/selectors/getProjectClosed';
import { getProjectDetails } from 'src/modules/project-view/selectors/getProjectDetails';
import { ShareProject } from 'src/modules/sharing-projects/containers/ShareProject';

export const BasedataPage = (): React.ReactElement => {
  const intl = useIntl();
  const project = useSelector(getProjectDetails);
  const isProjectClosed = useSelector(getProjectClosed);
  const formData = useSelector(getBasedataFormData);
  const annualSavingPlaceholderValue = useSelector(getAnnualSavingPlaceholderValue);

  const saveForm = useAction(BASEDATA_CHANGE.trigger);

  const form = useForm<BasedataFormData, FormError>('project.basedata', {
    reinitialize: true,
    initialValues: formData,
  });
  useFormValidator(form, BASEDATA_FORM_SCHEMA);

  useEffect(() => form.subscribe((prevState) => {
    const nextState = form.currentState;
    if (prevState.values !== nextState.values) {
      saveForm(nextState.values);
    }
  }), [form, saveForm]);

  const isNavigationDisabled = formData.annualSaving === null;

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({ id: 'basedata/pageTitle' })}
        </title>
      </Helmet>

      <AppLayout>

        <div className="bp-hvac-page">
          <div className="bp-hvac-page__content">
            <BreadcrumbContainer>
              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}`}
                title={intl.formatMessage({ id: 'project/toolbar/navigation/basedata' })}
                value={<FormattedMessage id="basedata/tabs/value" values={{ sub, sup }}/>}
                isEmpty={false}
                isFilled={false}
                disabled={false}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/emission`}
                title={intl.formatMessage({ id: 'hvac/tabs/emission/title' })}
                value={<FormattedMessage id="hvac/tabs/emission/value" values={{ sub, sup }}/>}
                isEmpty={true}
                isFilled={false}
                disabled={isNavigationDisabled}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/distribution`}
                title={intl.formatMessage({ id: 'hvac/tabs/distribution/title' })}
                value={<FormattedMessage id="hvac/tabs/distribution/value" values={{ sub, sup }}/>}
                isEmpty={true}
                disabled={isNavigationDisabled}
                isFilled={false}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/generation`}
                title={intl.formatMessage({ id: 'hvac/tabs/generation/title' })}
                value={<FormattedMessage id="hvac/tabs/generation/value" values={{ sub, sup }}/>}
                isEmpty={true}
                disabled={isNavigationDisabled}
                isFilled={false}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/results`}
                title={intl.formatMessage({ id: 'hvac/tabs/results/title' })}
                value={<FormattedMessage id="hvac/tabs/results/value" values={{ sub, sup }}/>}
                isEmpty={true}
                disabled={isNavigationDisabled}
                isFilled={false}
              />
            </BreadcrumbContainer>
            <BasedataFormView
              nextRouteDisabled={isNavigationDisabled}
              projectId={project.id}
              form={form}
              readOnly={project.readOnly}
              isProjectClosed={isProjectClosed}
              annualSavingPlaceholderValue={annualSavingPlaceholderValue}
            />
          </div>
        </div>
      </AppLayout>

      <ShareProject/>
    </>
  );
};
